import React, { useState, useEffect, useMemo, useCallback } from "react"
import {
    Row, Col, Card, CardBody, Button, Spinner, Alert, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"

/* ═══════════════════════════════════════════════════════════════
   ListarRoles — Gestión de Roles, Permisos y Menús
   ═══════════════════════════════════════════════════════════════ */
const ListarRoles = ({ setBreadcrumbItems, user, empresaSeleccionada, empresas: reduxEmpresas }) => {
    document.title = "Roles | LS-DA - Softlogy"

    // ── State: list ──
    const [roles, setRoles] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [successMsg, setSuccessMsg] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [filterEmpresaId, setFilterEmpresaId] = useState("")

    // ── State: crear/editar rol modal ──
    const [rolModalOpen, setRolModalOpen] = useState(false)
    const [rolEditando, setRolEditando] = useState(null) // null = crear
    const [rolNombre, setRolNombre] = useState("")
    const [rolDescripcion, setRolDescripcion] = useState("")
    const [rolEmpresaId, setRolEmpresaId] = useState("")
    const [rolSaving, setRolSaving] = useState(false)
    const [rolError, setRolError] = useState("")

    // ── State: asignar permisos modal ──
    const [permModalOpen, setPermModalOpen] = useState(false)
    const [permRol, setPermRol] = useState(null)
    const [todosPermisos, setTodosPermisos] = useState([])
    const [permisosSeleccionados, setPermisosSeleccionados] = useState([]) // array de IDs
    const [permLoading, setPermLoading] = useState(false)
    const [permSaving, setPermSaving] = useState(false)
    const [permError, setPermError] = useState("")

    // ── State: asignar menús modal ──
    const [menuModalOpen, setMenuModalOpen] = useState(false)
    const [menuRol, setMenuRol] = useState(null)
    const [todosMenus, setTodosMenus] = useState([])
    const [menusSeleccionados, setMenusSeleccionados] = useState([]) // array de IDs
    const [menuLoading, setMenuLoading] = useState(false)
    const [menuSaving, setMenuSaving] = useState(false)
    const [menuError, setMenuError] = useState("")

    // ── Breadcrumb ──
    useEffect(() => {
        const greeting = user ? `Bienvenido, ${user.nombre} ${user.apellido}` : "Dashboard"
        setBreadcrumbItems(greeting, [
            { title: "LS-DA", link: "#" },
            { title: "Seguridad", link: "#" },
            { title: "Roles", link: "#" },
        ])
    }, [setBreadcrumbItems, user])

    // ══════════════════════════════════════════════════════════════
    //  FETCH ROLES
    // ══════════════════════════════════════════════════════════════
    const fetchRoles = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const params = new URLSearchParams()
            if (filterEmpresaId) params.append("empresaId", filterEmpresaId)
            const qs = params.toString() ? `?${params.toString()}` : ""
            const res = await axiosClient.get(`roles${qs}`)
            const data = res.data.data || res.data
            setRoles(Array.isArray(data) ? data : [])
        } catch (err) {
            setError(err.response?.data?.message || "Error al obtener roles")
        } finally {
            setLoading(false)
        }
    }, [filterEmpresaId])

    useEffect(() => { fetchRoles() }, [fetchRoles])

    // ══════════════════════════════════════════════════════════════
    //  CREAR / EDITAR ROL
    // ══════════════════════════════════════════════════════════════
    const handleOpenCrear = () => {
        setRolEditando(null)
        setRolNombre("")
        setRolDescripcion("")
        setRolEmpresaId("")
        setRolError("")
        setRolModalOpen(true)
    }

    const handleOpenEditar = (rol) => {
        setRolEditando(rol)
        setRolNombre(rol.nombre || "")
        setRolDescripcion(rol.descripcion || "")
        setRolEmpresaId(rol.empresaId || "")
        setRolError("")
        setRolModalOpen(true)
    }

    const handleSaveRol = async () => {
        if (!rolNombre.trim()) { setRolError("El nombre es obligatorio"); return }
        setRolSaving(true)
        setRolError("")
        try {
            const payload = { nombre: rolNombre.trim(), descripcion: rolDescripcion.trim() }
            if (!rolEditando && rolEmpresaId) payload.empresaId = rolEmpresaId

            if (rolEditando) {
                await axiosClient.put(`roles/${rolEditando.id}`, payload)
                setSuccessMsg(`Rol "${rolNombre}" actualizado`)
            } else {
                await axiosClient.post("roles", payload)
                setSuccessMsg(`Rol "${rolNombre}" creado`)
            }
            setTimeout(() => setSuccessMsg(""), 3000)
            setRolModalOpen(false)
            fetchRoles()
        } catch (err) {
            const msg = err.response?.data?.message || "Error al guardar el rol"
            setRolError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setRolSaving(false)
        }
    }

    // ══════════════════════════════════════════════════════════════
    //  ASIGNAR PERMISOS
    // ══════════════════════════════════════════════════════════════
    const handleOpenPermisos = async (rol) => {
        setPermRol(rol)
        setPermError("")
        setPermModalOpen(true)
        setPermLoading(true)
        try {
            const res = await axiosClient.get("roles/permisos")
            const permisos = res.data.data || res.data || []
            setTodosPermisos(Array.isArray(permisos) ? permisos : [])
            // Pre-seleccionar los permisos actuales del rol
            const actuales = (rol.permisos || []).map(rp => rp.permisoId || rp.permiso?.id).filter(Boolean)
            setPermisosSeleccionados(actuales)
        } catch (err) {
            setPermError(err.response?.data?.message || "Error al cargar permisos")
        } finally {
            setPermLoading(false)
        }
    }

    const togglePermiso = (permisoId) => {
        setPermisosSeleccionados(prev =>
            prev.includes(permisoId) ? prev.filter(id => id !== permisoId) : [...prev, permisoId]
        )
    }

    const toggleModuloCompleto = (permisosMod) => {
        const ids = permisosMod.map(p => p.id)
        const todosSelec = ids.every(id => permisosSeleccionados.includes(id))
        if (todosSelec) {
            setPermisosSeleccionados(prev => prev.filter(id => !ids.includes(id)))
        } else {
            setPermisosSeleccionados(prev => [...new Set([...prev, ...ids])])
        }
    }

    const handleSavePermisos = async () => {
        setPermSaving(true)
        setPermError("")
        try {
            await axiosClient.put(`roles/${permRol.id}/permisos`, { permisoIds: permisosSeleccionados })
            setSuccessMsg(`Permisos asignados al rol "${permRol.nombre}"`)
            setTimeout(() => setSuccessMsg(""), 3000)
            setPermModalOpen(false)
            fetchRoles()
        } catch (err) {
            setPermError(err.response?.data?.message || "Error al asignar permisos")
        } finally {
            setPermSaving(false)
        }
    }

    // Agrupar permisos por módulo
    const permisosAgrupados = useMemo(() => {
        const grupos = {}
        todosPermisos.forEach(p => {
            const mod = p.modulo || "General"
            if (!grupos[mod]) grupos[mod] = []
            grupos[mod].push(p)
        })
        return grupos
    }, [todosPermisos])

    // ══════════════════════════════════════════════════════════════
    //  ASIGNAR MENÚS
    // ══════════════════════════════════════════════════════════════
    const handleOpenMenus = async (rol) => {
        setMenuRol(rol)
        setMenuError("")
        setMenuModalOpen(true)
        setMenuLoading(true)
        try {
            const res = await axiosClient.get("menus")
            const menus = res.data.data || res.data || []
            setTodosMenus(Array.isArray(menus) ? menus : [])
            // Pre-seleccionar menús actuales del rol
            const actuales = (rol.menus || []).map(rm => rm.menuId || rm.menu?.id).filter(Boolean)
            setMenusSeleccionados(actuales)
        } catch (err) {
            setMenuError(err.response?.data?.message || "Error al cargar menús")
        } finally {
            setMenuLoading(false)
        }
    }

    const toggleMenu = (menuId) => {
        setMenusSeleccionados(prev =>
            prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
        )
    }

    const toggleMenuPadreConHijos = (menu) => {
        const ids = [menu.id, ...(menu.hijos || []).map(h => h.id)]
        const todosSelec = ids.every(id => menusSeleccionados.includes(id))
        if (todosSelec) {
            setMenusSeleccionados(prev => prev.filter(id => !ids.includes(id)))
        } else {
            setMenusSeleccionados(prev => [...new Set([...prev, ...ids])])
        }
    }

    const handleSaveMenus = async () => {
        setMenuSaving(true)
        setMenuError("")
        try {
            await axiosClient.put(`roles/${menuRol.id}/menus`, { menuIds: menusSeleccionados })
            setSuccessMsg(`Menús asignados al rol "${menuRol.nombre}"`)
            setTimeout(() => setSuccessMsg(""), 3000)
            setMenuModalOpen(false)
            fetchRoles()
        } catch (err) {
            setMenuError(err.response?.data?.message || "Error al asignar menús")
        } finally {
            setMenuSaving(false)
        }
    }

    // ── Filtrar roles ──
    const filteredRoles = useMemo(() => {
        if (!searchTerm) return roles
        const t = searchTerm.toLowerCase()
        return roles.filter(r =>
            r.nombre?.toLowerCase().includes(t) || r.descripcion?.toLowerCase().includes(t)
        )
    }, [roles, searchTerm])

    const thStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }

    // ══════════════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════════════
    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                        <CardBody style={{ padding: "2rem" }}>
                            {/* ── Header ── */}
                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                <div>
                                    <h4 className="mb-1" style={{ fontWeight: 700 }}>
                                        <i className="mdi mdi-shield-account-outline me-2" style={{ color: "#556ee6" }} />
                                        Roles y Permisos
                                    </h4>
                                    <small className="text-muted">Administra los roles del sistema y asigna permisos y menús</small>
                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    {user?.esSuperAdmin && reduxEmpresas?.length > 0 && (
                                        <select
                                            value={filterEmpresaId}
                                            onChange={e => setFilterEmpresaId(e.target.value)}
                                            style={{
                                                padding: "6px 12px", borderRadius: 8,
                                                border: "1px solid #e2e8f0", fontSize: "0.85rem",
                                                outline: "none", backgroundColor: "#f8fafc", maxWidth: 250,
                                            }}
                                        >
                                            <option value="">Todas las empresas</option>
                                            {reduxEmpresas.map(emp => (
                                                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                                            ))}
                                        </select>
                                    )}
                                    <Button color="primary" onClick={handleOpenCrear} style={{ borderRadius: 8 }}>
                                        <i className="mdi mdi-plus me-1" />Nuevo Rol
                                    </Button>
                                </div>
                            </div>

                            {/* ── Alerts ── */}
                            {successMsg && (
                                <Alert color="success" className="mb-3">
                                    <i className="mdi mdi-check-circle me-1" />{successMsg}
                                </Alert>
                            )}
                            {error && (
                                <Alert color="danger" className="mb-3">
                                    <i className="mdi mdi-alert-circle me-1" />{error}
                                    <Button color="link" size="sm" className="ms-2 p-0" onClick={fetchRoles}>Reintentar</Button>
                                </Alert>
                            )}

                            {/* ── Search ── */}
                            <div style={{ position: "relative", marginBottom: 20 }}>
                                <i className="mdi mdi-magnify" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 18 }} />
                                <input
                                    type="text" value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Buscar roles por nombre o descripción..."
                                    style={{ width: "100%", padding: "10px 16px 10px 42px", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: "0.9rem", outline: "none", backgroundColor: "#f8fafc" }}
                                    onFocus={e => { e.target.style.borderColor = "#556ee6" }}
                                    onBlur={e => { e.target.style.borderColor = "#e2e8f0" }}
                                />
                            </div>

                            {/* ── Loading ── */}
                            {loading && (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Cargando roles...</p>
                                </div>
                            )}

                            {/* ── Table ── */}
                            {!loading && filteredRoles.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr style={{ backgroundColor: "#f8fafc" }}>
                                                <th style={thStyle}>Rol</th>
                                                <th style={thStyle}>Tipo</th>
                                                <th style={thStyle}>Empresa</th>
                                                <th style={thStyle}>Permisos</th>
                                                <th style={thStyle}>Menús</th>
                                                <th style={thStyle}>Usuarios</th>
                                                <th style={{ ...thStyle, width: 180 }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRoles.map(rol => (
                                                <tr key={rol.id}>
                                                    {/* Nombre + Desc */}
                                                    <td>
                                                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{rol.nombre}</div>
                                                        {rol.descripcion && (
                                                            <small style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{rol.descripcion}</small>
                                                        )}
                                                    </td>
                                                    {/* Tipo */}
                                                    <td>
                                                        {rol.esSistema ? (
                                                            <Badge color="warning" className="px-2 py-1" style={{ fontSize: "0.72rem" }}>
                                                                <i className="mdi mdi-lock-outline me-1" />Sistema
                                                            </Badge>
                                                        ) : (
                                                            <Badge color="info" className="px-2 py-1" style={{ fontSize: "0.72rem" }}>
                                                                Personalizado
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    {/* Empresa */}
                                                    <td>
                                                        <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                                                            {rol.empresaId
                                                                ? reduxEmpresas?.find(e => e.id === rol.empresaId)?.nombre || "Empresa"
                                                                : <Badge color="light" style={{ color: "#64748b", fontSize: "0.72rem" }}>Global</Badge>
                                                            }
                                                        </span>
                                                    </td>
                                                    {/* Permisos count */}
                                                    <td>
                                                        <Badge color="primary" className="px-2 py-1" style={{ fontSize: "0.75rem", cursor: "pointer" }}
                                                            onClick={() => handleOpenPermisos(rol)}>
                                                            <i className="mdi mdi-key-outline me-1" />
                                                            {rol.permisos?.length || 0}
                                                        </Badge>
                                                    </td>
                                                    {/* Menús count */}
                                                    <td>
                                                        <Badge color="success" className="px-2 py-1" style={{ fontSize: "0.75rem", cursor: "pointer" }}
                                                            onClick={() => handleOpenMenus(rol)}>
                                                            <i className="mdi mdi-menu me-1" />
                                                            {rol.menus?.length || 0}
                                                        </Badge>
                                                    </td>
                                                    {/* Usuarios count */}
                                                    <td>
                                                        <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                                                            {rol._count?.usuarios ?? "—"}
                                                        </span>
                                                    </td>
                                                    {/* Acciones */}
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Button color="outline-warning" size="sm" title="Editar Rol"
                                                                onClick={() => handleOpenEditar(rol)}
                                                                disabled={rol.esSistema}
                                                                style={{ borderRadius: 6 }}>
                                                                <i className="mdi mdi-pencil-outline" />
                                                            </Button>
                                                            <Button color="outline-primary" size="sm" title="Asignar Permisos"
                                                                onClick={() => handleOpenPermisos(rol)}
                                                                disabled={rol.esSistema}
                                                                style={{ borderRadius: 6 }}>
                                                                <i className="mdi mdi-key-outline" />
                                                            </Button>
                                                            <Button color="outline-success" size="sm" title="Asignar Menús"
                                                                onClick={() => handleOpenMenus(rol)}
                                                                disabled={rol.esSistema}
                                                                style={{ borderRadius: 6 }}>
                                                                <i className="mdi mdi-menu" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Empty ── */}
                            {!loading && filteredRoles.length === 0 && !error && (
                                <div className="text-center py-5">
                                    <i className="mdi mdi-shield-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
                                    <p className="text-muted mt-2 mb-0">
                                        {searchTerm ? "No se encontraron roles con ese criterio" : "No hay roles disponibles"}
                                    </p>
                                </div>
                            )}

                            {/* ── Count ── */}
                            {!loading && filteredRoles.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        Mostrando {filteredRoles.length} de {roles.length} rol{roles.length !== 1 ? "es" : ""}
                                    </small>
                                    <Button color="outline-secondary" size="sm" onClick={fetchRoles} style={{ borderRadius: 6 }}>
                                        <i className="mdi mdi-refresh me-1" />Actualizar
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ══════════════════════════════════════════════════════════
                MODAL: Crear/Editar Rol
            ══════════════════════════════════════════════════════════ */}
            <Modal isOpen={rolModalOpen} toggle={() => setRolModalOpen(false)} centered>
                <ModalHeader toggle={() => setRolModalOpen(false)}>
                    <i className={`mdi ${rolEditando ? "mdi-pencil" : "mdi-plus-circle"} me-2`} style={{ color: "#556ee6" }} />
                    {rolEditando ? `Editar: ${rolEditando.nombre}` : "Nuevo Rol"}
                </ModalHeader>
                <ModalBody>
                    {rolError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{rolError}
                        </Alert>
                    )}
                    <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                            Nombre del Rol <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <input type="text" className="form-control" value={rolNombre}
                            onChange={e => setRolNombre(e.target.value)}
                            placeholder="Ej: Operador Facturación"
                            style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>Descripción</label>
                        <textarea className="form-control" value={rolDescripcion}
                            onChange={e => setRolDescripcion(e.target.value)}
                            placeholder="Descripción del rol..."
                            rows={3}
                            style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem", resize: "vertical" }} />
                    </div>
                    {!rolEditando && (
                        <div className="mb-3">
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                                Empresa <small className="text-muted">(dejar vacío = rol global)</small>
                            </label>
                            <select value={rolEmpresaId} onChange={e => setRolEmpresaId(e.target.value)}
                                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: "0.9rem", outline: "none", backgroundColor: "#f8fafc" }}>
                                <option value="">Global (todas las empresas)</option>
                                {reduxEmpresas?.map(emp => (
                                    <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setRolModalOpen(false)} disabled={rolSaving}>Cancelar</Button>
                    <Button color="primary" size="sm" onClick={handleSaveRol} disabled={rolSaving} style={{ minWidth: 120, borderRadius: 8 }}>
                        {rolSaving ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="mdi mdi-content-save me-1" />{rolEditando ? "Actualizar" : "Crear Rol"}</>}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ══════════════════════════════════════════════════════════
                MODAL: Asignar Permisos
            ══════════════════════════════════════════════════════════ */}
            <Modal isOpen={permModalOpen} toggle={() => setPermModalOpen(false)} centered size="lg" scrollable>
                <ModalHeader toggle={() => setPermModalOpen(false)}>
                    <i className="mdi mdi-key-outline me-2" style={{ color: "#556ee6" }} />
                    Permisos: {permRol?.nombre}
                    <Badge color="primary" className="ms-2" style={{ fontSize: "0.75rem" }}>{permisosSeleccionados.length} seleccionados</Badge>
                </ModalHeader>
                <ModalBody>
                    {permError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{permError}
                        </Alert>
                    )}
                    {permLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Cargando permisos...</p>
                        </div>
                    ) : (
                        Object.entries(permisosAgrupados).map(([modulo, permisos]) => {
                            const todosSelec = permisos.every(p => permisosSeleccionados.includes(p.id))
                            const algunoSelec = permisos.some(p => permisosSeleccionados.includes(p.id))
                            return (
                                <div key={modulo} style={{ marginBottom: 20, border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                                    {/* Módulo header */}
                                    <div
                                        onClick={() => toggleModuloCompleto(permisos)}
                                        style={{
                                            padding: "10px 16px", backgroundColor: todosSelec ? "#eef2ff" : "#f8fafc",
                                            cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                                            borderBottom: "1px solid #e2e8f0",
                                        }}
                                    >
                                        <input type="checkbox" checked={todosSelec} readOnly
                                            ref={el => { if (el) el.indeterminate = algunoSelec && !todosSelec }}
                                            style={{ width: 16, height: 16, accentColor: "#556ee6", cursor: "pointer" }} />
                                        <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#334155" }}>{modulo}</span>
                                        <Badge color="light" style={{ color: "#64748b", fontSize: "0.7rem", marginLeft: "auto" }}>
                                            {permisos.filter(p => permisosSeleccionados.includes(p.id)).length}/{permisos.length}
                                        </Badge>
                                    </div>
                                    {/* Permisos */}
                                    <div style={{ padding: "8px 16px" }}>
                                        {permisos.map(p => (
                                            <div key={p.id}
                                                onClick={() => togglePermiso(p.id)}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                                                    cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                                                }}
                                            >
                                                <input type="checkbox" checked={permisosSeleccionados.includes(p.id)}
                                                    onChange={() => togglePermiso(p.id)}
                                                    style={{ width: 14, height: 14, accentColor: "#556ee6", cursor: "pointer" }} />
                                                <div>
                                                    <span style={{ fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>{p.nombre}</span>
                                                    <br />
                                                    <code style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{p.codigo}</code>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setPermModalOpen(false)} disabled={permSaving}>Cancelar</Button>
                    <Button color="primary" size="sm" onClick={handleSavePermisos} disabled={permSaving || permLoading} style={{ minWidth: 140, borderRadius: 8 }}>
                        {permSaving ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="mdi mdi-content-save me-1" />Guardar Permisos</>}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ══════════════════════════════════════════════════════════
                MODAL: Asignar Menús
            ══════════════════════════════════════════════════════════ */}
            <Modal isOpen={menuModalOpen} toggle={() => setMenuModalOpen(false)} centered size="lg" scrollable>
                <ModalHeader toggle={() => setMenuModalOpen(false)}>
                    <i className="mdi mdi-menu me-2" style={{ color: "#10b981" }} />
                    Menús: {menuRol?.nombre}
                    <Badge color="success" className="ms-2" style={{ fontSize: "0.75rem" }}>{menusSeleccionados.length} seleccionados</Badge>
                </ModalHeader>
                <ModalBody>
                    {menuError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{menuError}
                        </Alert>
                    )}
                    {menuLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Cargando menús...</p>
                        </div>
                    ) : (
                        todosMenus.map(menu => {
                            const tieneHijos = menu.hijos && menu.hijos.length > 0
                            const hijosSelec = tieneHijos ? menu.hijos.filter(h => menusSeleccionados.includes(h.id)).length : 0
                            const padreSelec = menusSeleccionados.includes(menu.id)

                            return (
                                <div key={menu.id} style={{ marginBottom: 12, border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                                    {/* Padre */}
                                    <div
                                        onClick={() => tieneHijos ? toggleMenuPadreConHijos(menu) : toggleMenu(menu.id)}
                                        style={{
                                            padding: "10px 16px", backgroundColor: padreSelec ? "#ecfdf5" : "#f8fafc",
                                            cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                                            borderBottom: tieneHijos ? "1px solid #e2e8f0" : "none",
                                        }}
                                    >
                                        <input type="checkbox" checked={padreSelec}
                                            onChange={() => tieneHijos ? toggleMenuPadreConHijos(menu) : toggleMenu(menu.id)}
                                            style={{ width: 16, height: 16, accentColor: "#10b981", cursor: "pointer" }} />
                                        {menu.icono && <i className={`${menu.icono}`} style={{ fontSize: 18, color: padreSelec ? "#10b981" : "#94a3b8" }} />}
                                        <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#334155" }}>{menu.nombre}</span>
                                        {menu.ruta && <code style={{ fontSize: "0.7rem", color: "#94a3b8", marginLeft: "auto" }}>{menu.ruta}</code>}
                                        {tieneHijos && (
                                            <Badge color="light" style={{ color: "#64748b", fontSize: "0.7rem", marginLeft: menu.ruta ? 8 : "auto" }}>
                                                {hijosSelec}/{menu.hijos.length}
                                            </Badge>
                                        )}
                                    </div>
                                    {/* Hijos */}
                                    {tieneHijos && (
                                        <div style={{ padding: "4px 16px 4px 44px" }}>
                                            {menu.hijos.map(hijo => (
                                                <div key={hijo.id}
                                                    onClick={() => toggleMenu(hijo.id)}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                                                        cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                                                    }}
                                                >
                                                    <input type="checkbox" checked={menusSeleccionados.includes(hijo.id)}
                                                        onChange={() => toggleMenu(hijo.id)}
                                                        style={{ width: 14, height: 14, accentColor: "#10b981", cursor: "pointer" }} />
                                                    <span style={{ fontSize: "0.85rem", color: "#475569" }}>{hijo.nombre}</span>
                                                    {hijo.ruta && <code style={{ fontSize: "0.7rem", color: "#94a3b8", marginLeft: "auto" }}>{hijo.ruta}</code>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        })
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setMenuModalOpen(false)} disabled={menuSaving}>Cancelar</Button>
                    <Button color="success" size="sm" onClick={handleSaveMenus} disabled={menuSaving || menuLoading} style={{ minWidth: 140, borderRadius: 8 }}>
                        {menuSaving ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="mdi mdi-content-save me-1" />Guardar Menús</>}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    user: state.Login.user,
    empresaSeleccionada: state.Login.empresaSeleccionada,
    empresas: state.Login.empresas,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ListarRoles)
